import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { motion } from 'framer-motion';
import { config } from '../config';
import { useAuth } from '../context/AuthContext';

const MotionPaper = motion(Paper);

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [showTwoFactorSetupDialog, setShowTwoFactorSetupDialog] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorSetupData, setTwoFactorSetupData] = useState(null);
  const [tempCredentials, setTempCredentials] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${config.endpoints.auth}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Login failed');
      }

      if (data.requiresTwoFactor) {
        setTempCredentials({ username, password });
        setShowTwoFactorDialog(true);
        setLoading(false);
        return;
      }

      if (data.requiresTwoFactorSetup) {
        setTempCredentials({ username, password });
        setTwoFactorSetupData(data);
        setShowTwoFactorSetupDialog(true);
        setLoading(false);
        return;
      }

      // Use the login function from AuthContext
      login(data.token, data.expiresIn);
      navigate('/admin');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${config.endpoints.auth}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: tempCredentials.username,
          password: tempCredentials.password,
          totpToken: twoFactorCode,
          ...(twoFactorSetupData?.setupToken ? { setupToken: twoFactorSetupData.setupToken } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Invalid 2FA code');
      }

      // Use the login function from AuthContext
      login(data.token, data.expiresIn);
      setShowTwoFactorDialog(false);
      setShowTwoFactorSetupDialog(false);
      navigate('/admin');
    } catch (error) {
      console.error('2FA error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        elevation={3}
        sx={{
          p: 4,
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: 'primary.main' }}>
          Admin Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.23)',
                },
              },
            }}
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.23)',
                },
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Box>
      </MotionPaper>

      {/* 2FA Dialog */}
      <Dialog open={showTwoFactorDialog} onClose={() => !loading && setShowTwoFactorDialog(false)}>
        <DialogTitle>Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enter 2FA Code"
            type="text"
            fullWidth
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTwoFactorDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleTwoFactorSubmit} disabled={loading || !twoFactorCode}>
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* First-login 2FA setup dialog after IONOS cutover */}
      <Dialog open={showTwoFactorSetupDialog} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Set up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            2FA needs to be re-enrolled on IONOS before admin access is allowed.
          </Alert>
          <Typography variant="body2" paragraph>
            Scan this QR code with your authenticator app, then enter the 6-digit code.
          </Typography>
          {twoFactorSetupData?.qrCode && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img src={twoFactorSetupData.qrCode} alt="2FA QR Code" style={{ maxWidth: '220px', width: '100%' }} />
            </Box>
          )}
          {twoFactorSetupData?.secret && (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 2, wordBreak: 'break-all' }}>
              Manual key: {twoFactorSetupData.secret}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Enter 2FA Code"
            type="text"
            fullWidth
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            variant="outlined"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowTwoFactorSetupDialog(false); setTwoFactorSetupData(null); setTwoFactorCode(''); }} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleTwoFactorSubmit} disabled={loading || twoFactorCode.length !== 6} variant="contained">
            {loading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default Login;
